import { useEffect, useRef } from "react";
import Konva from "konva";
import { KonvaEventObject } from "konva/lib/Node";
import { Rect, Transformer, Label, Text, Tag } from "react-konva";
import { clampRectPosition, getRectFromPoints } from "./geometry";

/* Prop Types */
interface RectangleProps {
  id: string;
  points: { x: number; y: number }[];
  label: string;
  labelColor: string;
  backgroundColor: string;
  strokeColor: string;
  ratio: number;
  isSelected?: boolean;
  canvasSize: { width: number; height: number };
  handleRegionSave: (id: string, x: number, y: number, width: number, height: number) => void;
  setSelectedRegion: (id: string) => void;
  onContextMenu?: (e: KonvaEventObject<PointerEvent>) => void;
  toolMode?: "pan" | "draw";
  scale: number;
}

/*
 * Rectangle Component
 * This component is used to render a rectangle AOI on the canvas in the editor component
 * It also handles the logic for moving, resizing, and saving the AOI to the store
 */
export const Rectangle = ({
  id,
  points,
  ratio,
  isSelected,
  handleRegionSave,
  canvasSize,
  label,
  labelColor,
  backgroundColor,
  strokeColor,
  setSelectedRegion,
  onContextMenu,
  toolMode = "pan",
  scale,
}: RectangleProps) => {
  /* Refs */
  const shapeRef = useRef<Konva.Rect>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const labelRef = useRef<Konva.Label>(null);

  /* If selected, attach the transformer and re-draw */
  useEffect(() => {
    if (isSelected && transformerRef.current) {
      transformerRef.current.nodes([shapeRef.current!]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  /* Handlers */
  const handleRegionSaveValues = (e: KonvaEventObject<DragEvent>) => {
    const { attrs } = e.target;

    // Scale canvas-space values back down before persisting
    handleRegionSave(id, attrs.x / ratio, attrs.y / ratio, attrs.width / ratio, attrs.height / ratio);
  };

  /* This keeps the label with the region when transforming or moving without it being scaled along with it */
  const handleLabelPosition = (e: KonvaEventObject<Event>) => {
    labelRef.current?.setAttrs({
      x: e.target.x() + 1,
      y: e.target.y() + 1,
    });
  };

  const handleTransformEnd = (e: KonvaEventObject<Event>) => {
    /* 
    The transformer is scaling the shape but isn't updating the width or height
    We do it manually here because we need the width and height when updating the store
    */
    const node = shapeRef.current;
    if (!node) return;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // reset scale
    node.scaleX(1);
    node.scaleY(1);

    // calculate the scaled width and height
    const scaledWidth = node.width() * scaleX;
    const scaledHeight = node.height() * scaleY;

    // if the shape is off the edge of the image or less than 0, set it's position to 0
    const newX = node.x() < 0 || scaledWidth > canvasSize.width ? 0 : node.x();
    const newY = node.y() < 0 || scaledHeight > canvasSize.height ? 0 : node.y();

    // if the shape is bigger than the image, set it to the size of the image otherwise set it to the scaled width
    const newWidth = scaledWidth > canvasSize.width ? canvasSize.width : scaledWidth;
    const newHeight = scaledHeight > canvasSize.height ? canvasSize.height : scaledHeight;

    node.setAttrs({
      x: newX,
      y: newY,
      // if the width or height is bigger than the image then cut it off otherwise set it to the new width or height
      width: node.x() + scaledWidth > canvasSize.width ? scaledWidth - (node.x() + scaledWidth - canvasSize.width) : newWidth,
      height: node.y() + scaledHeight > canvasSize.height ? scaledHeight - (node.y() + scaledHeight - canvasSize.height) : newHeight,
    });

    // keeps label in position when transforming off the edge of the image
    handleLabelPosition(e);

    // save the region
    handleRegionSave(id, node.x() / ratio, node.y() / ratio, node.width() / ratio, node.height() / ratio);
  };

  const handleDragMove = (e: KonvaEventObject<Event>) => {
    const targetX = e.target.x();
    const targetY = e.target.y();
    const targetWidth = e.target.width();
    const targetHeight = e.target.height();

    const { x: newX, y: newY } = clampRectPosition({
      x: targetX,
      y: targetY,
      width: targetWidth,
      height: targetHeight,
      canvasSize,
    });

    shapeRef.current?.x(newX);
    shapeRef.current?.y(newY);

    // keep label with the shape
    handleLabelPosition(e);
  };

  // if the points are empty, return null
  if (points?.length === 0) return null;

  const rect = getRectFromPoints(points);

  /* Render */
  return (
    <>
      <Rect
        draggable={toolMode === "pan" && !!isSelected}
        ref={shapeRef}
        x={rect.x * ratio}
        y={rect.y * ratio}
        width={rect.width * ratio}
        height={rect.height * ratio}
        stroke={strokeColor}
        strokeWidth={2}
        onClick={toolMode === "pan" ? () => setSelectedRegion(id) : undefined}
        onDragMove={handleDragMove}
        onDragEnd={handleRegionSaveValues}
        onTransformEnd={handleTransformEnd}
        fill={backgroundColor}
        onContextMenu={onContextMenu}
        listening={toolMode === "pan"}
      />
      <Label x={rect.x * ratio} y={rect.y * ratio} ref={labelRef} onClick={toolMode === "pan" ? () => setSelectedRegion(id) : undefined} listening={toolMode === "pan"}>
        <Tag fill={labelColor} />
        <Text text={label} fontSize={14} fontStyle="bold" padding={6 * ratio} fill="white" fontFamily="Inter" />
      </Label>

      {isSelected && toolMode === "pan" && (
        <Transformer
          ref={transformerRef}
          rotateEnabled={false}
          keepRatio={false}
          onTransform={handleLabelPosition}
          flipEnabled={false}
          borderStroke={strokeColor}
          borderStrokeWidth={2 * scale}
          anchorSize={10 * scale}
          anchorCornerRadius={2}
          anchorFill="white"
          anchorStroke={strokeColor}
          anchorStrokeWidth={2 * scale}
        />
      )}
    </>
  );
};
